import { useState } from "react";
import { Bell, Loader2, ShieldCheck, BellOff } from "lucide-react";
import SettingsLayout from "./SettingsLayout";
import useSettingsProfile from "./useSettingsProfile";

const labelMap = {
  granted: { label: "Enabled", tone: "bg-emerald-50 text-emerald-700" },
  denied: { label: "Blocked", tone: "bg-rose-50 text-rose-700" },
  default: { label: "Not enabled", tone: "bg-amber-50 text-amber-700" },
};

export default function NotificationSettings() {
  const { loading } = useSettingsProfile();
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const [requesting, setRequesting] = useState(false);
  const [notice, setNotice] = useState("");

  const requestBrowserNotifications = async () => {
    if (typeof Notification === "undefined") {
      setNotice("Your browser does not support notifications.");
      return;
    }
    setRequesting(true); setNotice("");
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result === "granted") setNotice("Browser notifications enabled for new chat messages.");
      else if (result === "denied") setNotice("Notifications were blocked. Enable them in browser site settings.");
    } finally {
      setRequesting(false);
    }
  };

  const status = labelMap[permission] || labelMap.default;

  return (
    <SettingsLayout
      title="Notifications"
      description="Manage how Karanki alerts you. Chat alerts use your browser when the app is open or in the background."
    >
      {loading ? (
        <div className="card flex justify-center p-10">
          <Loader2 className="animate-spin text-brand-600" size={28} />
        </div>
      ) : (
        <>
          <div className="card p-5 sm:p-6">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-gradient text-white shadow-soft">
                <Bell size={22} />
              </div>
              <div className="flex-1">
                <h2 className="font-display text-lg font-bold text-ink-800">Chat messages</h2>
                <p className="mt-1 text-[13.5px] leading-6 text-ink-500">
                  Get a desktop or mobile alert when someone sends you a new message. You can also enable this from the Chats page.
                </p>
                <span className={`mt-3 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11.5px] font-bold ${status.tone}`}>
                  {permission === "granted" ? <ShieldCheck size={11} /> : <BellOff size={11} />}
                  Status: {status.label}
                </span>
              </div>
            </div>

            {permission === "default" && (
              <button
                type="button"
                onClick={requestBrowserNotifications}
                disabled={requesting}
                className="btn-primary mt-5 w-full py-3.5"
              >
                {requesting ? "Requesting…" : "Enable browser notifications"}
              </button>
            )}

            {permission === "denied" && (
              <p className="mt-4 rounded-2xl bg-amber-50 px-4 py-3 text-[12.5px] leading-6 text-amber-700">
                Open your browser settings → Site permissions → Notifications, and allow notifications for this site.
              </p>
            )}
          </div>

          <ul className="card mt-4 space-y-3 p-5 text-[13.5px] leading-6 text-ink-500 sm:p-6">
            <li>• Interest requests and acceptances appear inside the app.</li>
            <li>• Profile verification updates appear on your dashboard.</li>
            <li>• SMS or email alerts can be added when your team enables them.</li>
          </ul>

          {notice && (
            <p className="mt-3 rounded-2xl bg-sand-100 px-4 py-3 text-[13px] text-ink-700">{notice}</p>
          )}
        </>
      )}
    </SettingsLayout>
  );
}
